import React, { useEffect, useState, useCallback, useMemo } from "react";
import { bookingsAPI } from "../services/api";
import toast from "react-hot-toast";
import { CheckCircle, RefreshCw, Edit2, XCircle, ChevronLeft, ChevronRight } from "lucide-react";

const PAGE_SIZE = 10;

interface LLDBooking {
  _id: string;
  roomName: string;
  checkInDate: string;
  checkInTime: string;
  checkOutDate: string;
  checkOutTime: string;
  noteCheckInTime: string;
  noteCheckOutTime: string;
  shiftLabel: string;
  guestName: string;
}

interface EditState {
  checkInTime: string;
  checkOutTime: string;
}

export default function LLDTimeReviewPage() {
  const [bookings, setBookings] = useState<LLDBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editState, setEditState] = useState<EditState>({ checkInTime: "", checkOutTime: "" });
  const [submitting, setSubmitting] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await bookingsAPI.getLLDTimeReview();
      const sorted = [...res.data.data].sort((a: LLDBooking, b: LLDBooking) =>
        b.checkInDate.localeCompare(a.checkInDate)
      );
      setBookings(sorted);
    } catch {
      toast.error("Không tải được danh sách duyệt giờ");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (
    id: string,
    action: "approve" | "keep" | "edit",
    extra?: { checkInTime: string; checkOutTime: string }
  ) => {
    setSubmitting(id);
    try {
      await bookingsAPI.approveNoteTime(id, { action, ...extra });
      setBookings(prev => prev.filter(b => b._id !== id));
      setEditingId(null);
      if (action === "approve") toast.success("Đã duyệt giờ theo Note");
      else if (action === "keep") toast.success("Giữ nguyên giờ cũ");
      else toast.success("Đã cập nhật giờ mới");
    } catch {
      toast.error("Lỗi khi xử lý");
    } finally {
      setSubmitting(null);
    }
  };

  const startEdit = (b: LLDBooking) => {
    setEditingId(b._id);
    setEditState({ checkInTime: b.noteCheckInTime, checkOutTime: b.noteCheckOutTime });
  };

  const formatDate = (d: string) => {
    const [y, m, day] = d.split("-");
    return `${day}/${m}/${y}`;
  };

  const timeDiffers = (b: LLDBooking) =>
    b.noteCheckInTime !== b.checkInTime || b.noteCheckOutTime !== b.checkOutTime;

  const totalPages = Math.ceil(bookings.length / PAGE_SIZE);
  const pageBookings = useMemo(
    () => bookings.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [bookings, page]
  );

  // Reset to page 1 when bookings list changes (after approve/keep/edit removes an item)
  useEffect(() => {
    if (page > 1 && pageBookings.length === 0) setPage(p => p - 1);
  }, [page, pageBookings.length]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">Duyệt giờ LLD Sheet</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Các đơn LLD có giờ ở cột Note khác với giờ ca mặc định, cần admin xác nhận
          </p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-2 px-3 py-2 text-sm bg-white border border-zinc-200 rounded-lg hover:bg-zinc-50 text-zinc-700"
        >
          <RefreshCw size={14} />
          Tải lại
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-400">Đang tải...</div>
      ) : bookings.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-zinc-400 gap-3">
          <CheckCircle size={40} className="text-emerald-400" />
          <p className="font-medium">Không có đơn nào cần duyệt giờ</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50">
                <th className="text-left px-4 py-3 font-semibold text-zinc-600 w-28">Ngày</th>
                <th className="text-left px-4 py-3 font-semibold text-zinc-600">Phòng</th>
                <th className="text-left px-4 py-3 font-semibold text-zinc-600">Ca</th>
                <th className="text-left px-4 py-3 font-semibold text-zinc-600">Khách</th>
                <th className="text-left px-4 py-3 font-semibold text-zinc-600">Ca mặc định</th>
                <th className="text-left px-4 py-3 font-semibold text-zinc-600">Giờ Note</th>
                <th className="text-right px-4 py-3 font-semibold text-zinc-600">Thao tác</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {pageBookings.map(b => {
                const differs = timeDiffers(b);
                const isEditing = editingId === b._id;
                const busy = submitting === b._id;

                return (
                  <React.Fragment key={b._id}>
                    <tr className={`hover:bg-zinc-50 transition-colors ${isEditing ? "bg-indigo-50/40" : ""}`}>
                      {/* Ngày */}
                      <td className="px-4 py-3 text-zinc-700 font-medium whitespace-nowrap">
                        {formatDate(b.checkInDate)}
                        {b.checkOutDate !== b.checkInDate && (
                          <span className="text-zinc-400 text-xs block">→ {formatDate(b.checkOutDate)}</span>
                        )}
                      </td>

                      {/* Phòng */}
                      <td className="px-4 py-3">
                        <span className="font-semibold text-zinc-900">{b.roomName}</span>
                        <span className="ml-2 text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">LLD</span>
                      </td>

                      {/* Ca */}
                      <td className="px-4 py-3 text-zinc-500 whitespace-nowrap">{b.shiftLabel}</td>

                      {/* Khách */}
                      <td className="px-4 py-3 text-zinc-700">{b.guestName}</td>

                      {/* Ca mặc định */}
                      <td className="px-4 py-3 font-mono whitespace-nowrap">
                        <span className={differs ? "text-zinc-400 line-through" : "text-zinc-800"}>
                          {b.checkInTime} – {b.checkOutTime}
                        </span>
                      </td>

                      {/* Giờ Note */}
                      <td className="px-4 py-3 font-mono whitespace-nowrap">
                        {b.noteCheckInTime ? (
                          <span className="text-amber-700 font-medium">
                            {b.noteCheckInTime} – {b.noteCheckOutTime}
                          </span>
                        ) : (
                          <span className="text-zinc-300">—</span>
                        )}
                      </td>

                      {/* Thao tác */}
                      <td className="px-4 py-3">
                        {!isEditing && (
                          <div className="flex items-center gap-2 justify-end flex-wrap">
                            {differs && (
                              <button
                                onClick={() => handleAction(b._id, "approve")}
                                disabled={busy}
                                className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg hover:bg-emerald-100 disabled:opacity-50 font-medium whitespace-nowrap"
                              >
                                <CheckCircle size={12} />
                                Duyệt Note
                              </button>
                            )}
                            <button
                              onClick={() => startEdit(b)}
                              disabled={busy}
                              className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-lg hover:bg-indigo-100 disabled:opacity-50 whitespace-nowrap"
                            >
                              <Edit2 size={12} />
                              Sửa giờ
                            </button>
                            <button
                              onClick={() => handleAction(b._id, "keep")}
                              disabled={busy}
                              className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-zinc-50 text-zinc-600 border border-zinc-200 rounded-lg hover:bg-zinc-100 disabled:opacity-50 whitespace-nowrap"
                            >
                              <XCircle size={12} />
                              Giữ nguyên
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>

                    {/* Inline edit row */}
                    {isEditing && (
                      <tr className="bg-indigo-50/60 border-t-0">
                        <td colSpan={7} className="px-4 py-3">
                          <div className="flex items-center gap-3 flex-wrap">
                            <span className="text-xs font-medium text-zinc-600">Chỉnh giờ:</span>
                            <label className="text-xs text-zinc-500">Nhận:</label>
                            <input
                              type="time"
                              value={editState.checkInTime}
                              onChange={e => setEditState(s => ({ ...s, checkInTime: e.target.value }))}
                              className="text-sm border border-zinc-300 rounded px-2 py-1 bg-white"
                            />
                            <label className="text-xs text-zinc-500">Trả:</label>
                            <input
                              type="time"
                              value={editState.checkOutTime}
                              onChange={e => setEditState(s => ({ ...s, checkOutTime: e.target.value }))}
                              className="text-sm border border-zinc-300 rounded px-2 py-1 bg-white"
                            />
                            <button
                              onClick={() => handleAction(b._id, "edit", editState)}
                              disabled={busy}
                              className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                            >
                              Lưu
                            </button>
                            <button
                              onClick={() => setEditingId(null)}
                              className="px-3 py-1.5 text-sm bg-white border border-zinc-200 text-zinc-700 rounded-lg hover:bg-zinc-50"
                            >
                              Huỷ
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-100 bg-zinc-50">
              <span className="text-xs text-zinc-500">
                {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, bookings.length)} / {bookings.length} đơn
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(p => p - 1)}
                  disabled={page === 1}
                  className="p-1.5 rounded-lg border border-zinc-200 bg-white hover:bg-zinc-100 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={14} />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`w-8 h-8 text-xs rounded-lg border font-medium transition-colors ${
                      p === page
                        ? "bg-indigo-600 text-white border-indigo-600"
                        : "bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-100"
                    }`}
                  >
                    {p}
                  </button>
                ))}
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={page === totalPages}
                  className="p-1.5 rounded-lg border border-zinc-200 bg-white hover:bg-zinc-100 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
